import { buildApiUrl } from "../../config.js";
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { FaTimes, FaExchangeAlt } from "react-icons/fa";
import Select from "react-select";

const TransferMoney = ({
  clientId,
  fetchPaymentSummary,
  setLoading,
  closeModal,
}) => {
  const [senderId, setSenderId] = useState(null);
  const [receiverId, setReceiverId] = useState(null);
  const [amount, setAmount] = useState("");
  const [clients, setClients] = useState([]);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await axios.get(
          buildApiUrl("/api/clients"),
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        setClients(response.data);
      } catch (error) {
        console.error("Error fetching clients:", error);
        toast.error("Error fetching client list. Please try again.");
      }
    };

    fetchClients();
  }, []);

  const handleTransfer = async () => {
    if (
      !senderId?.value ||
      !receiverId?.value ||
      !amount ||
      isNaN(amount) ||
      amount <= 0
    ) {
      toast.error("Please fill out all fields correctly!");
      return;
    }

    setLoading(true);
    try {
      const transferData = {
        senderId: senderId.value,
        receiverId: receiverId.value,
        amount: Number(amount),
      };

      const response = await axios.post(
        buildApiUrl("/api/payment/admin/transfer"),
        transferData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      fetchPaymentSummary(senderId.value, setLoading);
      setSenderId(null);
      setReceiverId(null);
      setAmount("");
      toast.success("Money transferred successfully!");
      closeModal();
    } catch (error) {
      console.error("Error transferring money:", error);
      toast.error("Error transferring money. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Convert clients to react-select options format
  const clientOptions = clients.map((client) => ({
    value: client.user,
    label: `${client.firmName} - ${client.username}`,
  }));

  // Custom styles for react-select to match existing styling
  const customStyles = {
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    control: (provided) => ({
      ...provided,
      minHeight: "34px",
      height: "34px",
      borderRadius: "6px",
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: "34px",
      padding: "0 4px",
    }),
    input: (provided) => ({
      ...provided,
      margin: 0,
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: "34px",
    }),
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-70 z-50 transition-opacity duration-300">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-sm mx-4 p-4 transform transition-all duration-300 scale-100">
        <div className="flex items-center justify-between border-b border-gray-200 pb-2">
          <div className="flex items-center gap-2">
            <FaExchangeAlt className="text-indigo-600" size={16} />
            <h2 className="text-lg font-semibold text-gray-800">
              Transfer Money
            </h2>
          </div>
          <button
            onClick={closeModal}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <FaTimes size={16} />
          </button>
        </div>

        <div className="mt-3 space-y-3">
          <div>
            <label
              htmlFor="senderId"
              className="block text-xs font-medium text-gray-600 mb-1"
            >
              Sender
            </label>
            <Select
              id="senderId"
              value={senderId}
              onChange={setSenderId}
              options={clientOptions}
              styles={customStyles}
              placeholder="Select Sender Client ID"
              isClearable
            />
          </div>

          <div>
            <label
              htmlFor="receiverId"
              className="block text-xs font-medium text-gray-600 mb-1"
            >
              Receiver
            </label>
            <Select
              id="receiverId"
              value={receiverId}
              onChange={setReceiverId}
              options={clientOptions}
              styles={customStyles}
              placeholder="Select Receiver Client ID"
              isClearable
            />
          </div>

          <div>
            <label
              htmlFor="amount"
              className="block text-xs font-medium text-gray-600 mb-1"
            >
              Amount
            </label>
            <input
              id="amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
              placeholder="Enter Amount"
            />
          </div>

          <div className="flex justify-end items-center pt-3 border-t border-gray-200 gap-2">
            <button
              onClick={closeModal}
              className="px-3 py-2 bg-gray-200 text-gray-700 text-xs rounded-md hover:bg-gray-300 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              onClick={handleTransfer}
              className="px-3 py-2 bg-indigo-600 text-white text-xs rounded-md hover:bg-indigo-800 transition-colors duration-200 flex items-center gap-1"
            >
              <FaExchangeAlt size={12} />
              Transfer Money
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(TransferMoney);
